import { Button } from '@dfh-corp-sites/ui'
import { useLocation } from '@gatsbyjs/reach-router'
import React from 'react'

import { ProtocolResourceQuery } from '../../../graphql/api'
import { Dispensary } from '../../../graphql/camel'
import { useClickToCopy } from '../../../hooks/useClickToCopy'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { addDispensaryBasePathToPath } from '../../../lib/dispensaries'
import { useDispensaryByCodeEager } from '../../../lib/dispensaries/hooks/useDispensaryByCodeEager'
import { usePractitionerSettingsEager } from '../../../lib/practitioner-settings/hooks/usePractitionerSettingsEager'
import Spinner from '../../Spinner'

const DispensaryNotFoundMessage: React.FC = () => (
  <div>
    <p>
      Your Virtual Dispensary is currently not set up. Please reach out to our Customer Experience
      team at{' '}
      <a className="text-brand-red" href="tel:18606236314">
        860-623-6314
      </a>{' '}
      for assistance. Once your account is all set up, you will be able to prescribe protocols to
      your patients using generated eScript links.
    </p>
  </div>
)

interface DispensaryEScriptLinkProps {
  dispensary: Dispensary
  products: NonNullable<ProtocolResourceQuery['protocolResource']>['products']
}

interface ClickToCopyProps {
  copyText: string
}

const CopyLink: React.FC<ClickToCopyProps> = ({ copyText }) => {
  const { copyToClipboard } = useClickToCopy(copyText)

  return (
    <>
      <div className="flex justify-between items-center gap-6">
        <div className="w-[150px] shrink-0">Share this link with your patients</div>
        <div className="hidden md:block border border-black rounded-lg py-3 px-3.5 text-[#6A6A6A] text-sm max-w-[495px] truncate">
          {copyText}
        </div>
        <Button onClick={copyToClipboard} className="sm:py-2 sm:px-5 font-medium sm:text-lg">
          Copy Link
        </Button>
      </div>
      <div className="md:hidden mt-4 border border-black rounded-lg py-3 px-3.5 text-[#6A6A6A] text-sm max-w-[495px] truncate">
        {copyText}
      </div>
    </>
  )
}

export const DispensaryEScriptLink: React.FC<DispensaryEScriptLinkProps> = React.memo(
  ({ dispensary, products }) => {
    const location = useLocation()
    const productsString = products?.map(
      (product) => `(${product?.product?.sku}*${product?.quantity})`,
    )
    const productsParams = productsString?.join('')
    const versionParams = 'v=2023-04-11'
    const paramsString = `${versionParams}&products=${productsParams}`
    const scriptParams = new URLSearchParams(paramsString)
    const link = `${location.origin}${addDispensaryBasePathToPath(
      dispensary.slug,
      'checkout',
    )}?${scriptParams}`
    return <CopyLink copyText={link} />
  },
)

DispensaryEScriptLink.displayName = 'DispensaryEScriptLink'

interface EScriptLinkProps {
  products: NonNullable<ProtocolResourceQuery['protocolResource']>['products']
}

export const EScriptLink: React.FC<EScriptLinkProps> = ({ products }) => {
  const {
    data: settings,
    loading: settingsLoading,
    error: settingsError,
  } = usePractitionerSettingsEager()
  useLogAndCaptureError(settingsError)
  const practitionerCode = settings?.practitioner?.partnerCode
  const {
    loading: dispLoading,
    data: dispensary,
    error: dispError,
  } = useDispensaryByCodeEager({
    practitionerCode,
    skip: !!settings?.practitioner?.optedOutOfDispensary,
  })
  useLogAndCaptureError(dispError)

  return settingsLoading || dispLoading ? (
    <Spinner loading />
  ) : dispensary ? (
    <DispensaryEScriptLink dispensary={dispensary} products={products} />
  ) : (
    <DispensaryNotFoundMessage />
  )
}
